import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash, Plus, Save, X } from "lucide-react";
import { usePermissionTemplates, type PermissionTemplateRow } from "@/lib/queries";
import { useCreateTemplate, useUpdateTemplate, useDeleteTemplate } from "@/lib/mutations";
import { PermissionMatrix } from "./PermissionMatrix";

export function PermissionTemplateManager({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { data, isLoading } = usePermissionTemplates();
  const [editing, setEditing] = useState<PermissionTemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const del = useDeleteTemplate();
  const templates = data?.templates ?? [];

  const close = () => {
    setEditing(null);
    setCreating(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-maroon-700 dark:text-amber-500">Permission Templates</DialogTitle>
          <DialogDescription>Manage role templates (create, edit, and delete custom permission bundles).</DialogDescription>
        </DialogHeader>

        {editing || creating ? (
          <TemplateEditor
            template={editing}
            onDone={() => { setEditing(null); setCreating(false); }}
          />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted">{templates.length} template(s).</p>
              <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => setCreating(true)}>
                <Plus size={14} className="mr-1" /> New template
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Grants</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={4}>Loading…</TableCell></TableRow>
                ) : templates.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted py-6">No templates</TableCell></TableRow>
                ) : templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">
                      {t.name}
                      {t.description && <div className="text-xs text-muted">{t.description}</div>}
                    </TableCell>
                    <TableCell>
                      {t.isSystem ? <Badge>system</Badge> : <Badge variant="outline">custom</Badge>}
                    </TableCell>
                    <TableCell>{t.permissionIds.length}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(t)}>
                          <Pencil size={14} />
                        </Button>
                        {!t.isSystem && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del.mutate(t.id)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateEditor({ template, onDone }: { template: PermissionTemplateRow | null; onDone: () => void }) {
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(template?.permissionIds ?? []));
  const create = useCreateTemplate();
  const update = useUpdateTemplate();

  const save = async () => {
    try {
      if (template) {
        const body: { name?: string; description?: string; permissionIds?: string[] } = {
          permissionIds: Array.from(selectedIds),
        };
        if (!template.isSystem && name && name !== template.name) body.name = name;
        if (description !== (template.description ?? "")) body.description = description;
        await update.mutateAsync({ id: template.id, body });
      } else {
        if (!name.trim()) return;
        await create.mutateAsync({ name, description: description || undefined, permissionIds: Array.from(selectedIds) });
      }
      onDone();
    } catch { /* handled */ }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} disabled={template?.isSystem} />
          {template?.isSystem && <p className="mt-1 text-xs text-muted">System template names are locked.</p>}
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </div>
      </div>
      <PermissionMatrix selectedIds={selectedIds} onChange={setSelectedIds} />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}><X size={14} className="mr-1" /> Cancel</Button>
        <Button onClick={save} disabled={create.isPending || update.isPending || (!template && !name.trim())} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
          <Save size={14} className="mr-1" /> Save
        </Button>
      </div>
    </div>
  );
}
